import type { PropsWithChildren } from 'react';
import type { RxCollection, RxDatabase } from 'rxdb';
import type { LanguageObject } from '@/constants/translations';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IntlProvider } from 'react-intl';

import { languages } from '@/constants/translations';
import { getTranslations } from '@/services/webservices';
import { CURRENT_LANGUAGE_KEY, setStorageItem } from '@/stores/localstorage';

type TranslationProviderProps = PropsWithChildren<{
	currentLanguage: LanguageObject[ 'key' ];
	db: RxDatabase;
}>;

type TranslationContextProps = {
	currentLocale: LanguageObject[ 'key' ];
	locales: LanguageObject[];
	switchLocale: ( locale: LanguageObject[ 'key' ] ) => void;
};

const TranslationContext = createContext<TranslationContextProps | undefined>( undefined );

// This function exists to suppress errors while testing because it's very chatty
function errorHandler( error: any ) {
	if ( error.code !== 'MISSING_TRANSLATION' && error.code !== 'MISSING_DATA' ) {
		console.error( error );
	}
}

async function downloadTranslations( translations: RxCollection ) {
	const translationData = await getTranslations();

	const transformedData = translationData.map( ( language: any ) => {
		let valueObj:any = {};

		language.value.forEach( ({ key, value } : { key: string; value: string; }) => {
			valueObj[ key ] = value;
		});

		return {
			key: language.key,
			value: JSON.stringify( valueObj )
		};
	});

	await translations.bulkUpsert( transformedData );
}

function TranslationProvider({
	children,
	currentLanguage,
	db
}: TranslationProviderProps ) {
	const translationsCollection = db.translations;

	const [ currentLocale, setCurrentLocale ] = useState<LanguageObject[ 'key' ]>( currentLanguage || 'en' );
	const [ currentTranslations, setCurrentTranslations ] = useState<undefined | any>();

	const translationsDownloaded = useRef( false );

	const switchLocale = useCallback( ( locale: LanguageObject[ 'key' ] = 'en' ) => {
		if ( locale === currentLocale ) {
			return;
		}

		setStorageItem( CURRENT_LANGUAGE_KEY, locale );

		setCurrentLocale( locale );
	}, [ currentLocale, setCurrentLocale ] );

	const loadTranslationsFromDb = useCallback( async ( translations: RxCollection, locale: LanguageObject[ 'key' ] ) => {
		if ( !translations ) {
			return;
		}

		const currentTranslationsRecord = await translations.findOne({
			selector: { key: locale }
		}).exec();

		if ( !currentTranslationsRecord ) {
			return;
		}

		const record = await currentTranslationsRecord.toJSON();

		setCurrentTranslations( JSON.parse( record.value ) );
	}, [ setCurrentTranslations ] );

	const fetchTranslations = useCallback( async ( translations: RxCollection, locale: LanguageObject[ 'key' ] ) => {
		await downloadTranslations( translationsCollection );

		translationsDownloaded.current = true;

		loadTranslationsFromDb( translations, locale );
	}, [ loadTranslationsFromDb, translationsCollection ] );


	useEffect( () => {
		if ( !translationsCollection || !currentLocale ) {
			return;
		}

		if ( !translationsDownloaded.current ) {
			fetchTranslations( translationsCollection, currentLocale );
		} else {
			loadTranslationsFromDb( translationsCollection, currentLocale );
		}
	}, [ currentLocale, translationsCollection ] );


	const returnValue: TranslationContextProps = useMemo( () => ({
		currentLocale,
		locales: languages,
		switchLocale
	}), [ switchLocale ] );

	return (
		<TranslationContext.Provider value={ returnValue }>
			{ currentTranslations && (
				<IntlProvider
					messages={ currentTranslations }
					locale={ currentLocale }
					defaultLocale="en"
					onError={ errorHandler }
				>
					{ children }
				</IntlProvider>
			)}
		</TranslationContext.Provider>
	);
}

function useTranslation() {
	const context = useContext( TranslationContext );

	if ( context === undefined ) {
		throw new Error( 'useTranslation must be used within a TranslationProvider' );
	}

	return context;
}

export { TranslationProvider, useTranslation };
